import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { authAPI } from '../lib/api';
import { getToken, getUser, setSession, updateUser as persistUser, clearSession, setLogoutReason } from '../lib/authStorage';

const AuthContext = createContext(null);

const DEFAULT_SESSION_TIMEOUT_MIN = 30;
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'];

// Berechtigungs-Stufen
const LEVELS = { none: 0, view: 1, execute: 2, edit: 3 };

// Backward-compatible Boolean-Aliasse → minimale Permission-Stufe
const BOOLEAN_PERMISSION_MAP = {
  canManageEmployees: ['employees', 'edit'],
  canApproveVacations: ['vacations', 'execute'],
  canManageShifts: ['shifts', 'edit'],
  canViewSensitiveData: ['sensitive_data', 'view'],
  canManageTimeTracking: ['time_tracking', 'edit'],
  canManageAbsences: ['absences', 'edit'],
  canManageDocuments: ['documents', 'edit'],
  canManageSettings: ['settings', 'edit'],
  canViewCompliance: ['compliance', 'view'],
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const timeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Role Preview state
  const [previewRole, setPreviewRole] = useState(null);

  const logout = useCallback((reason) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (typeof reason === 'string') setLogoutReason(reason);
    setPreviewRole(null);
    clearSession();
    setUser(null);
  }, []);

  const resetTimeout = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (user) {
      const minutes = Number(user.session_timeout_minutes) || DEFAULT_SESSION_TIMEOUT_MIN;
      timeoutRef.current = setTimeout(() => {
        console.log('Session expired due to inactivity');
        logout('inactivity');
      }, minutes * 60 * 1000);
    }
  }, [user, logout]);

  useEffect(() => {
    if (user) {
      ACTIVITY_EVENTS.forEach(event => {
        window.addEventListener(event, resetTimeout, { passive: true });
      });
      resetTimeout();
    }
    return () => {
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, resetTimeout);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [user, resetTimeout]);

  const loadUser = useCallback(async () => {
    const token = getToken();
    const savedUser = getUser();

    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
        const response = await authAPI.getMe();
        setUser(response.data);
        persistUser(response.data);
      } catch (err) {
        console.error('Token validation failed:', err);
        clearSession();
        setUser(null);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (email, password, remember = true) => {
    setError(null);
    try {
      const response = await authAPI.login({ email, password });
      const { access_token, user: userData } = response.data;
      setSession(access_token, userData, remember);
      setUser(userData);
      return userData;
    } catch (err) {
      const message = err.response?.data?.detail || 'Anmeldung fehlgeschlagen';
      setError(message);
      throw new Error(message);
    }
  };

  // Login via Passkey/Fingerabdruck — Token + User kommen vom Backend.
  const loginWithToken = (access_token, userData, remember = true) => {
    setSession(access_token, userData, remember);
    setUser(userData);
    return userData;
  };

  // ==================== PREVIEW MODE ====================
  const startPreview = useCallback((role) => {
    setPreviewRole({
      id: role.id,
      name: role.name,
      color: role.color,
      scope_type: role.scope_type,
      permissions: role.permissions || {},
    });
  }, []);

  const stopPreview = useCallback(() => {
    setPreviewRole(null);
  }, []);

  const isInPreview = !!previewRole;

  // ==================== PERMISSION LOGIC ====================
  const realIsAdmin = user?.role === 'admin';

  // In preview mode, override admin status and permissions
  const isAdmin = isInPreview ? false : realIsAdmin;
  const isBranchManager = user?.role === 'branch_manager';
  const isLocationManager = user?.role === 'location_manager';
  const isManager = isInPreview
    ? false
    : ['manager', 'admin', 'branch_manager', 'location_manager'].includes(user?.role);
  const isEmployee = isInPreview ? true : user?.role === 'employee';

  // Use preview permissions when in preview mode
  const permissionsMap = useMemo(() => (isInPreview
    ? (previewRole?.permissions || {})
    : (user?.effective_permissions?.permissions || {})),
    [isInPreview, previewRole?.permissions, user?.effective_permissions?.permissions]);

  const hasPermission = useCallback((area, minLevel = 'view') => {
    if (!isInPreview && realIsAdmin) return true;
    const userLevel = permissionsMap[area] || 'none';
    return (LEVELS[userLevel] || 0) >= (LEVELS[minLevel] || 0);
  }, [isInPreview, realIsAdmin, permissionsMap]);

  // Backward-compatible Boolean-Helper aus BOOLEAN_PERMISSION_MAP generieren
  const booleanPermissions = Object.fromEntries(
    Object.entries(BOOLEAN_PERMISSION_MAP).map(([key, [area, level]]) => [key, hasPermission(area, level)])
  );

  // Scope info
  const scopeType = isInPreview
    ? (previewRole.scope_type || 'own')
    : (realIsAdmin ? 'all' : (user?.effective_permissions?.scope_type || 'own'));
  const allowedBranchIds = user?.effective_permissions?.branch_ids || [];
  const allowedLocationIds = user?.effective_permissions?.location_ids || [];

  const value = {
    user,
    setUser,
    loading,
    error,
    login,
    loginWithToken,
    logout,
    mustSetup: user?.must_change_password === true,
    // Role checks (affected by preview)
    isAdmin,
    isBranchManager,
    isLocationManager,
    isManager,
    isEmployee,
    isAuthenticated: !!user,
    // True admin check (not affected by preview)
    realIsAdmin,
    // Level-based permission system
    hasPermission,
    permissionsMap,
    // Backward-compatible booleans (auto-generiert)
    permissions: permissionsMap,
    ...booleanPermissions,
    // Scope
    scopeType,
    allowedBranchIds,
    allowedLocationIds,
    // Preview
    previewRole,
    isInPreview,
    startPreview,
    stopPreview,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
